// Obra › Clima con el cronograma de la obra (el Gantt que el cliente subió en Obra › Datos de la obra; el backend
// lo lee una sola vez al subirlo). Con la fecha de hoy se toman las actividades en curso o que arrancan pronto y,
// para cada una, se miran los días previstos con el pronóstico: si un día no conviene, se busca el mejor día
// cercano. Sin cronograma, el Planificador queda como siempre.
//   "Hormigonado losa 3° piso, previsto 12–14/11: el jueves 13 llueve 80 %, conviene el miércoles 12"
// Todo con las reglas de reglasClima.js (sin IA, sin red).
import R from './reglasClima';

/** Tipo de actividad del cronograma → tarea del recomendador (null = día de obra general, 'no' = no depende del clima). */
export const TAREA_DE_TIPO = {
  hormigonado: 'hormigonado',
  suelos: 'suelos',
  impermeabilizacion: 'impermeabilizacion',
  pintura: 'pintura',
  estructura: 'grua',
  mamposteria: null,
  interior: 'no',
};
export const TIPO_TXT = {
  hormigonado: 'Hormigonado', suelos: 'Movimiento de suelos', mamposteria: 'Mampostería', impermeabilizacion: 'Impermeabilización',
  pintura: 'Pintura exterior', estructura: 'Estructura metálica / grúa', interior: 'Interior',
};
export const TIPO_EMOJI = { hormigonado: '🚛', suelos: '🚜', mamposteria: '🧱', impermeabilizacion: '🛡️', pintura: '🎨', estructura: '🏗️', interior: '🏠' };

const diaNombre = (f) => R.fechaLarga(f).replace(/ de [a-záéíóú]+$/i, ''); // "jueves 13"
const dm = (f) => `${Number(f.slice(8, 10))}/${Number(f.slice(5, 7))}`;
export const rangoTxt = (a) => (a.inicio === a.fin ? dm(a.fin) : a.inicio.slice(0, 7) === a.fin.slice(0, 7) ? `${Number(a.inicio.slice(8, 10))}–${dm(a.fin)}` : `${dm(a.inicio)}–${dm(a.fin)}`);
const minus = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

/** Actividades sensibles al clima en curso o que empiezan en los próximos `dias` días (por fecha de inicio). */
export function proximasActividades(cronograma, hoy, { dias = 14, max = 6 } = {}) {
  if (!cronograma || cronograma.estado !== 'listo' || !Array.isArray(cronograma.actividades)) return [];
  const hasta = R.addDias(hoy, dias);
  return cronograma.actividades
    .filter((a) => a && a.inicio && a.fin && a.fin >= hoy && a.inicio <= hasta && TAREA_DE_TIPO[a.tipo] !== 'no')
    .sort((a, b) => (a.inicio < b.inicio ? -1 : a.inicio > b.inicio ? 1 : 0))
    .slice(0, max);
}

/** Cómo viene un día para esa actividad: { nivel: ideal|posible|no, puntaje, motivo } o null si no hay pronóstico. */
export function evaluarParaActividad(tipo, ix, fecha, U) {
  const tarea = TAREA_DE_TIPO[tipo];
  if (tarea) {
    const t = R.evaluarTarea(tarea, ix, fecha, U);
    if (!t) return null;
    const falla = t.criterios.find((c) => !c.ok && !c.aviso);
    return { nivel: t.nivel, puntaje: t.puntaje, motivo: falla ? falla.texto : null };
  }
  const e = R.evaluarDia(ix, fecha, U);
  if (!e || !e.estado) return null;
  const m = e.motivos.find((x) => x.estado === e.estado) || e.motivos[0];
  return { nivel: e.estado === 'apto' ? 'ideal' : e.estado === 'cuidado' ? 'posible' : 'no', puntaje: e.estado === 'apto' ? 90 : e.estado === 'cuidado' ? 65 : 20, motivo: m ? m.texto : null };
}

/**
 * Recomendación para una actividad: mira sus días previstos (desde hoy, días laborales, con pronóstico) y, si hay
 * días que no convienen, propone el mejor día laboral cercano (hasta 3 días antes o después de lo previsto).
 */
export function recomendar(act, ix, U, hoy, ultimoPron) {
  const desde = act.inicio > hoy ? act.inicio : hoy;
  const laboral = (f) => U.diasLaborales.includes(R.diaIso(f));
  const dias = [];
  for (let f = desde; f <= act.fin && dias.length < 21; f = R.addDias(f, 1)) if (laboral(f)) dias.push(f);
  const base = { act, rango: rangoTxt(act), enCurso: act.inicio <= hoy, tarea: TAREA_DE_TIPO[act.tipo] };
  if (!dias.length) return { ...base, estado: 'sin-dias', texto: 'No hay días laborales previstos de acá en adelante.' };
  const conPron = dias.filter((f) => f <= ultimoPron).map((f) => ({ fecha: f, ev: evaluarParaActividad(act.tipo, ix, f, U) })).filter((x) => x.ev);
  if (!conPron.length) return { ...base, estado: 'sin-pronostico', texto: `Todavía no hay pronóstico para esas fechas (llega hasta el ${R.fechaCorta(ultimoPron)}).` };
  const malos = conPron.filter((x) => x.ev.nivel === 'no');
  const buenos = conPron.filter((x) => x.ev.nivel !== 'no');
  if (!malos.length) {
    const mejor = [...buenos].sort((a, b) => b.ev.puntaje - a.ev.puntaje)[0];
    const todos = conPron.length === dias.length;
    return { ...base, estado: 'ok', dias: conPron, mejor, texto: `${todos ? 'El pronóstico acompaña los días previstos' : 'Los días con pronóstico vienen bien'}${conPron.length > 1 ? `; el mejor, el ${diaNombre(mejor.fecha)}` : ''}.` };
  }
  // Día malo: buscar el mejor día laboral con pronóstico, entre 3 días antes del inicio previsto y 3 después del fin.
  const ini = R.addDias(act.inicio, -3) > hoy ? R.addDias(act.inicio, -3) : hoy;
  const cands = [];
  for (let f = ini; f <= R.addDias(act.fin, 3) && f <= ultimoPron; f = R.addDias(f, 1)) {
    if (!laboral(f)) continue;
    const ev = evaluarParaActividad(act.tipo, ix, f, U);
    if (ev && ev.nivel !== 'no') cands.push({ fecha: f, ev, dentro: f >= act.inicio && f <= act.fin });
  }
  const malo = malos[0];
  const cerca = (x) => Math.abs(Date.parse(x.fecha) - Date.parse(malo.fecha));
  const alt = cands.sort((a, b) => (b.ev.nivel === 'ideal') - (a.ev.nivel === 'ideal') || (b.dentro - a.dentro) || cerca(a) - cerca(b) || b.ev.puntaje - a.ev.puntaje)[0];
  const porque = malo.ev.motivo ? `: ${minus(malo.ev.motivo)}` : ' no conviene';
  const otros = malos.length > 1 ? ` (y ${malos.length - 1} ${malos.length === 2 ? 'día más' : 'días más'})` : '';
  const conviene = alt ? `, conviene el ${diaNombre(alt.fecha)}${alt.dentro ? '' : ' (fuera de lo previsto)'}` : '. No vemos un día bueno cerca: conviene reprogramar';
  return { ...base, estado: 'mover', dias: conPron, malos, alternativa: alt || null, texto: `El ${diaNombre(malo.fecha)}${porque}${otros}${conviene}.` };
}
